/**
 * Shadow-DOM aware re-exports of the react-bootstrap components used by the
 * ported ChatOBI screens.
 *
 * `Modal` and `OverlayTrigger` portal their content into `document.body` by
 * default, which is outside the MFE's shadow root — the markup would render
 * unstyled in the host page. Both accept a `container`, so it is defaulted to
 * the shadow root here and every legacy screen imports from this module
 * instead of `react-bootstrap` directly.
 */
import { ComponentProps, useMemo } from 'react';
import { useShadowRoot } from '@access-evo/evo-api';
import { Modal as BsModal, OverlayTrigger as BsOverlayTrigger } from 'react-bootstrap';

export { Accordion, Button, Col, Dropdown, Fade, Form, Row, Tooltip } from 'react-bootstrap';

const PORTAL_CONTAINER_ID = 'chatobi-portal-root';

function usePortalContainer(): HTMLElement | undefined {
  const shadowRoot = useShadowRoot();

  return useMemo(() => {
    if (!shadowRoot) return undefined;

    const existing = shadowRoot.getElementById(PORTAL_CONTAINER_ID);
    if (existing) return existing;

    const container = document.createElement('div');
    container.id = PORTAL_CONTAINER_ID;
    shadowRoot.appendChild(container);
    return container;
  }, [shadowRoot]);
}

type ModalProps = ComponentProps<typeof BsModal>;

function ShadowModal({ container, ...props }: ModalProps) {
  const portalContainer = usePortalContainer();
  return <BsModal container={container ?? portalContainer} {...props} />;
}

export const Modal = Object.assign(ShadowModal, {
  Header: BsModal.Header,
  Title: BsModal.Title,
  Body: BsModal.Body,
  Footer: BsModal.Footer,
  Dialog: BsModal.Dialog,
  TRANSITION_DURATION: BsModal.TRANSITION_DURATION,
  BACKDROP_TRANSITION_DURATION: BsModal.BACKDROP_TRANSITION_DURATION,
});

type OverlayTriggerProps = ComponentProps<typeof BsOverlayTrigger>;

export function OverlayTrigger({ container, ...props }: OverlayTriggerProps) {
  const portalContainer = usePortalContainer();
  return <BsOverlayTrigger container={container ?? portalContainer} {...props} />;
}
